import { useState } from "react";
import { useQueryClient } from "react-query";
import { useIntercom } from "react-use-intercom";

import renderTermsOfService from "../Organisms/Modals/Terms of Service/terms-of-service";

import { useModalContext } from "../Contexts/ModalContext";

import useStakePay3AuthToken from "./useStakePay3AuthToken";

import {
  useAppEssentials,
  useConsumerDeals,
  useDeals,
  useMobileAppSummary,
  usePlatformSummaryStats,
  usePrograms,
  useTransferToken,
  useUnclaimedTransactions,
  useUserAccount,
} from "./Queries";

import { LocalStorage } from "../Utils/helpers";
import { APIQueryKeys, appUtilityKeys } from "../Utils/constants";

export default function usePrefetchOnLogin({ authKey, hasVisitedWelcomeScreen }) {
  const queryClient = useQueryClient();
  const modalContext = useModalContext();

  const { update } = useIntercom();

  const { modalVisibility } = modalContext;

  const [hasTransferToken, setHasTransferToken] = useState(!!LocalStorage.read(appUtilityKeys.transferToken));

  const {
    isLoading: isLoadingDeals,
    isSuccess: isDealsFetchSuccess,
    isError: isDealsFetchError,
    error: dealsError,
    data: dealsData,
  } = useDeals({
    enabled: !!authKey,
    onSuccess: ({ data }) => {
      LocalStorage.write(appUtilityKeys.userSfid, data?.data?.currentDeal?.sfid);
      if (data?.data?.currentDeal?.show_tos_flag && !modalVisibility) renderTermsOfService(modalContext);
    },
  });

  const {
    isLoading: isLoadingConsumerDeals,
    isSuccess: isConsumerDealsFetchSuccess,
    isError: isConsumerDealsFetchError,
    error: consumerDealsError,
    data: consumerDealsData,
  } = useConsumerDeals({ enabled: !!authKey && hasTransferToken });

  const {
    isLoading: isLoadingUserAccountData,
    isSuccess: isUserAccountDataFetchSuccess,
    isError: isUserAccountDataFetchError,
    error: userAccountError,
    data: userAccountData,
  } = useUserAccount({
    enabled: !!authKey,
    onSuccess: (userAccountData) => {
      !!authKey &&
        update({
          userId: userAccountData?.data?.data?.user?._id,
          name: `${userAccountData?.data?.data?.user?.firstName} ${userAccountData?.data?.data?.user?.lastName} there`,
          email: userAccountData?.data?.data?.user?.email,
          phone: userAccountData?.data?.data?.user?.phoneNumber,
        });
    },
  });

  const {
    isLoading: isTransferTokenLoading,
    isSuccess: isTransferTokenFetchSuccess,
    isError: isTransferTokenFetchError,
    error: transferTokenError,
    data: transferTokenData,
  } = useTransferToken({
    onSuccess: ({ data }) => {
      LocalStorage.write(appUtilityKeys.transferToken, data?.data?.token);
      setHasTransferToken(!!data?.data?.token);
      queryClient.refetchQueries(APIQueryKeys.unclaimedTransactions);
    },
    enabled: !!authKey && !hasTransferToken,
  });

  const {
    isLoading: isLoadingUnclaimedTransactions,
    isSuccess: isUnclaimedTransactionsFetchSuccess,
    isError: isUnclaimedTransactionsFetchError,
    data: unclaimedTransactionsData,
    error: unclaimedTransactionsError,
  } = useUnclaimedTransactions({ enabled: !!authKey && hasTransferToken });

  const {
    isLoading: isLoadingMobileAppSummaryAPI,
    isSuccess: isMobileAppSummaryFetchSuccess,
    isError: isMobileAppSummaryFetchError,
    error: mobileAppSummaryError,
    data: mobileAppSummaryData,
  } = useMobileAppSummary({
    enabled: !hasVisitedWelcomeScreen && !!authKey,
  });

  const {
    isLoading: isLoadingPlatformSummaryStatsAPI,
    isSuccess: isPlatformSummaryStatsFetchSuccess,
    isError: isPlatformSummaryStatsFetchError,
    error: platformSummaryStatsError,
    data: platformSummaryStatsData,
  } = usePlatformSummaryStats({ enabled: !hasVisitedWelcomeScreen && !!authKey });

  const {
    isLoadingAuthToken: isLoadingStakePayAuthToken,
    stakePay3AuthData: { isError: isStakePayAuthTokenError, error: stakePay3AuthError, data: stakePay3AuthTokenData },
  } = useStakePay3AuthToken(!!authKey && hasTransferToken);

  const {
    isLoading: isLoadingAppEssentials,
    data: appEssentialsData,
    isSuccess: isAppEssentialsFetchSuccess,
    isError: isAppEssentialsFetchError,
    error: appEssentialsFetchError,
  } = useAppEssentials({ enabled: !!authKey && hasTransferToken });

  const propertyHasProgram = appEssentialsData?.propertyHasProgram ?? false;

  const {
    isLoading: isLoadingPrograms,
    data: programsData,
    isSuccess: isProgramsFetchSuccess,
    isError: isProgramsFetchError,
    error: programsFetchError,
  } = usePrograms({ enabled: !!authKey && hasTransferToken && propertyHasProgram });

  return {
    isLoading:
      isLoadingDeals ||
      isLoadingConsumerDeals ||
      isLoadingUserAccountData ||
      isTransferTokenLoading ||
      isLoadingUnclaimedTransactions ||
      isLoadingMobileAppSummaryAPI ||
      isLoadingPlatformSummaryStatsAPI ||
      isLoadingStakePayAuthToken ||
      isLoadingAppEssentials ||
      isLoadingPrograms,
    isSuccess:
      isDealsFetchSuccess &&
      isConsumerDealsFetchSuccess &&
      isUserAccountDataFetchSuccess &&
      isTransferTokenFetchSuccess &&
      isUnclaimedTransactionsFetchSuccess &&
      isAppEssentialsFetchSuccess &&
      (!propertyHasProgram || (propertyHasProgram && isProgramsFetchSuccess)) &&
      (!!hasVisitedWelcomeScreen ||
        (!hasVisitedWelcomeScreen && isMobileAppSummaryFetchSuccess && isPlatformSummaryStatsFetchSuccess)),
    isError:
      isDealsFetchError ||
      isConsumerDealsFetchError ||
      isUserAccountDataFetchError ||
      isTransferTokenFetchError ||
      isUnclaimedTransactionsFetchError ||
      isStakePayAuthTokenError ||
      isAppEssentialsFetchError ||
      (propertyHasProgram && isProgramsFetchError) ||
      (!hasVisitedWelcomeScreen && (isMobileAppSummaryFetchError || isPlatformSummaryStatsFetchError)),
    errorStatus: isDealsFetchError
      ? dealsError?.response?.status
      : isConsumerDealsFetchError
      ? consumerDealsError?.response?.status
      : isUserAccountDataFetchError
      ? userAccountError?.response?.status
      : isTransferTokenFetchError
      ? transferTokenError?.response?.status
      : isUnclaimedTransactionsFetchError
      ? unclaimedTransactionsError?.response?.status
      : isMobileAppSummaryFetchError
      ? mobileAppSummaryError?.response?.status
      : isPlatformSummaryStatsFetchError
      ? platformSummaryStatsError?.response?.status
      : isStakePayAuthTokenError
      ? stakePay3AuthError?.response?.status
      : isAppEssentialsFetchError
      ? appEssentialsFetchError?.response?.status
      : isProgramsFetchError
      ? programsFetchError?.response?.status
      : null,
    data: {
      dealsData,
      consumerDealsData,
      userAccountData,
      transferTokenData,
      unclaimedTransactionsData,
      mobileAppSummaryData,
      platformSummaryStatsData,
      stakePay3AuthTokenData,
      appEssentialsData,
      programsData,
    },
    hasUnclaimedTransactionsData: unclaimedTransactionsData?.data?.data?.unclaimed_transactions?.length > 0,
  };
}
